<template>
    <div>
        <v-row dense>
            <v-col cols="12" sm class="recipient-search">
                <v-autocomplete
                    v-model="model"
                    :items="addresses"
                    :loading="isBusy"
                    :search-input.sync="keywords"
                    prepend-icon="mdi-notebook"
                    :label="$t('shopping_cart.recipient')"
                    :hint="$t('shopping_cart.start_typing_any_recipient_attribute')"
                    item-value="address_external_id"
                    item-text="address_external_id"
                    class="mb-3"
                    persistent-hint
                    hide-no-data
                    return-object
                    clearable
                    outlined
                    dense
                    no-filter
                    @change="onSelectAddress"
                >
                    <template v-slot:prepend-item>
                        <div v-if="!singleRecipient" class="mt-1 mx-3 mb-3">
                            <v-btn color="secondary" outlined small block @click="onSelectAll()">
                                <v-icon left>mdi-account-multiple-plus</v-icon>
                                {{ $t('shopping_cart.add_all_recipients') }}
                            </v-btn>
                        </div>
                        <v-divider v-if="!singleRecipient"/>
                    </template>
                    <template v-slot:item="{ item }">
                        {{ addressString(item) }}
                    </template>
                </v-autocomplete>
            </v-col>
            <v-col v-if="allowCreate" cols="12" sm="auto" class="mb-3">
                <v-btn
                    color="primary"
                    outlined
                    block
                    height="40"
                    :disabled="!userId || !warehouseId"
                    @click="createDialog = true"
                >
                    <v-icon left>mdi-account-plus</v-icon>
                    {{ $t('shopping_cart.new_recipient') }}
                </v-btn>
            </v-col>
        </v-row>
        <create-recipient-dialog
            v-if="allowCreate && warehouseId"
            v-model="createDialog"
            :warehouse-id="warehouseId"
            @created="onRecipientCreated"
        />
    </div>
</template>

<script>
import ShoppingCartMixin from '@/mixins/ShoppingCartMixin'
import BulkOrderMixin from '@/mixins/BulkOrderMixin'
import {API} from '@/api/api'
import SnackbarMixin from "@/mixins/SnackbarMixin";
import _ from "lodash";
import CreateRecipientDialog from '@/components/user/shoppingCart/CreateRecipientDialog';
import {addressString, cartRecipient} from '@/utils/cartRecipient';

export default {
    name: "AddRecipientComponent",
    mixins: [SnackbarMixin, ShoppingCartMixin, BulkOrderMixin],
    components: {CreateRecipientDialog},
    props: {
        userId: {
            type: Number,
            required: true
        },
        warehouseId: {
            type: Number,
            required: true
        },
        storeModule: {
            type: String,
            required: false,
            default: () => "shoppingCart"
        },
        singleRecipient: {
            type: Boolean,
            default: () => false
        },
        allowCreate: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        // loading
        isBusyAddresses: 0,
        createDialog: false,
        // autocomplete model
        model: null,
        // autocomplete value
        keywords: null,
        // data
        addresses: [],
        // autocomplete request counter
        requestCounter: 0
    }),
    watch: {
        keywords: _.debounce(function (keywords) {
            const requestNumber = (this.requestCounter += 1);
            if (!keywords || keywords.length < 1) {
                this.addresses = [];
                return;
            }

            const url = "/api/addresses/by-warehouse/" + encodeURI(this.warehouseId)
                + "?page=1"
                + "&perPage=100"
                + "&filter=" + encodeURIComponent(keywords);

            this.isBusyAddresses += 1;
            API.get(url)
                .then(response => {
                    if (requestNumber === this.requestCounter) {
                        this.addresses = response.data;
                    }
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyAddresses -= 1;
                });
        }, 250),
        warehouseId() {
            this.createDialog = false;
            this.clearSearch();
        }
    },
    computed: {
        isBusy() {
            return this.isBusyAddresses > 0;
        }
    },
    methods: {
        addressString(address) {
            return addressString(address);
        },
        clearSearch() {
            this.requestCounter += 1;
            this.model = null;
            this.keywords = null;
            this.addresses = [];
        },
        onRecipientCreated(address) {
            this.selectAddress(address);
            this.clearSearch();
            this.snackbarSuccess(this.$t('shopping_cart.recipient_created'));
        },
        onSelectAll() {
            if (this.singleRecipient) {
                return;
            }
            this.addresses.forEach(address => this.selectAddress(address));
            this.clearSearch();
        },
        onSelectAddress() {
            if (!this.model) {
                return;
            }
            this.selectAddress(this.model);
            this.clearSearch();
        },
        selectAddress(address) {
            // get recipient's contact info from previous orders (from same bulk order)
            const recipient = this.getRecipientsOfBulkOrders(this.userId, this.warehouseId)
                .find(r => Number(r.address_id) === Number(address.address_external_id));

            const data = cartRecipient(address, recipient);
            this.addRecipientToShoppingCart(this.userId, this.warehouseId, data, this.storeModule);
            this.$emit("recipient-selected", data);
        }
    }
}

</script>

<style scoped>
.recipient-search {
    min-width: 0;
}
</style>
